<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import ContactActionBar from './ContactActionBar.vue';
import AdOriginDetails from 'dashboard/components/widgets/conversation/AdOriginDetails.vue';
import { formatPhoneDisplay } from 'dashboard/helper/phoneDisplay';
import ContactAPI from 'dashboard/api/contacts';
import Avatar from 'next/avatar/Avatar.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PanelCard from 'dashboard/components-next/panel/PanelCard.vue';
import ConversationAction from '../ConversationAction.vue';
import { startOutboundStackZap } from 'dashboard/composables/useStackZapCallSession';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    ContactActionBar,
    AdOriginDetails,
    EditContact,
    InlineInput,
    Avatar,
    SocialIcons,
    PanelCard,
    ConversationAction,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    // SiteUp: o botão "Agendar ação" só existe quando o painel dono sabe
    // agendar (a conversa está carregada e a feature está ligada na conta).
    canSchedule: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['panelClose', 'schedule'],
  setup() {
    const { isAdmin } = useAdmin();
    const { isWorkspaceV2 } = useWorkspaceV2();
    return { isAdmin, isWorkspaceV2 };
  },
  data() {
    return {
      showEditModal: false,
      showDeleteModal: false,
      showAdInfo: false,
      isEditingName: false,
      editName: '',
      contactIdentities: [],
      isLoadingIdentities: false,
      showOtherIdentities: false,
      identityRequestToken: 0,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
      getInbox: 'inboxes/getInbox',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentAccountId: 'getCurrentAccountId',
    }),
    currentInbox() {
      return this.getInbox(this.currentChat?.inbox_id);
    },
    // SiteUp V2 (2026-08-10): bandeira do país (derivada do E.164, nunca
    // hardcoded) + formato nacional. A bandeira substitui o rótulo — era o
    // rótulo comprido que truncava o número em 320px.
    phoneDisplayValue() {
      return formatPhoneDisplay(this.contact.phone_number);
    },
    // Se a conta contratou voz E a caixa atual sabe ligar. A ausência de
    // telefone NÃO entra aqui de propósito: quem não tem número vê o botão
    // apagado com o motivo escrito (ContactActionBar), não um botão sumido —
    // era exatamente esse sumiço que o painel escondia antes.
    canCall() {
      if (
        !this.isFeatureEnabledonAccount(
          this.currentAccountId,
          FEATURE_FLAGS.VOIP_CALLS
        )
      )
        return false;
      const inbox = this.currentInbox;
      const isWaha =
        inbox?.waha_backed || inbox?.additional_attributes?.waha_session_name;
      if (!isWaha) return false;
      // Device operational check foi removido: `inbox.voip_device` é derivado
      // no serializer e o cache IndexedDB não tem esse campo pra inboxes cachadas
      // antes da Phase 3. Backend valida device operacional no POST /voip_calls
      // e retorna erro claro. Se feature foi habilitada + inbox é WAHA, presume-se
      // que admin cadastrou device.
      // Webphone oficial gerencia state internamente — botão sempre habilitado
      // quando condições acima satisfazem.
      return true;
    },
    hasContactIdentities() {
      return this.isFeatureEnabledonAccount(
        this.currentAccountId,
        FEATURE_FLAGS.CONTACT_IDENTITIES
      );
    },
    sortedContactIdentities() {
      return [...this.contactIdentities].sort((a, b) => {
        if (a.identity_type !== b.identity_type) {
          return a.identity_type === 'phone' ? -1 : 1;
        }
        if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
        return String(a.value).localeCompare(String(b.value));
      });
    },
    otherIdentities() {
      return this.sortedContactIdentities.filter(identity => {
        if (identity.identity_type === 'phone') {
          return identity.value !== this.contact.phone_number;
        }
        if (identity.identity_type === 'email') {
          return identity.value !== this.contact.email;
        }
        return false;
      });
    },
    identitySummaryLabel() {
      const phoneCount = this.otherIdentities.filter(
        identity => identity.identity_type === 'phone'
      ).length;
      const emailCount = this.otherIdentities.filter(
        identity => identity.identity_type === 'email'
      ).length;
      return this.$t('CONTACT_PANEL.IDENTITIES.SUMMARY', {
        phones: phoneCount,
        emails: emailCount,
      });
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    // SiteUp V2: o anúncio que originou ESTA conversa. Mora em
    // conversation.custom_attributes (CtwaReferralProcessor), não no contato —
    // a mesma pessoa pode voltar por outro anúncio amanhã.
    adAttributes() {
      return this.currentChat?.custom_attributes || {};
    },
    // "Infos" também mostra o resumo que a IA escreveu no card (pedido do
    // Lucas, 2026-08-11). Mesma regra de card primário do KanbanActions:
    // lead_primary vence, senão o primeiro.
    primaryKanbanDescription() {
      const items = this.currentChat?.kanban_items || [];
      if (!items.length) return '';
      const primary = items.find(i => i.item_details?.lead_primary) || items[0];
      return primary?.item_details?.description || '';
    },
    showBusinessBadge() {
      return Boolean(
        this.additionalAttributes.waha_business &&
          this.isFieldVisible('whatsapp_business')
      );
    },
    // SiteUp F1.5b: rótulo da origem do contato. Hoje só Meta (anúncio/post);
    // outros canais entram na mesma chave depois, sem mexer na tela.
    contactOriginLabel() {
      const { origin_channel: channel, origin_label: label } =
        this.additionalAttributes;
      if (!channel) return '';
      const channelName =
        channel === 'meta_post'
          ? this.$t('CONTACT_PANEL.ORIGIN_META_POST')
          : this.$t('CONTACT_PANEL.ORIGIN_META_AD');
      return label ? `${channelName} · ${label}` : channelName;
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    // SiteUp (Phase C-2/3): endereço montado a partir dos additional_attributes.
    formattedAddress() {
      const a = this.additionalAttributes;
      return [
        a.address,
        a.address_number,
        a.address_complement,
        a.neighborhood,
        a.zipcode ? `CEP ${a.zipcode}` : '',
        a.state,
      ]
        .filter(Boolean)
        .join(', ');
    },
    // SiteUp (Phase C-3): campos que o usuário escondeu do painel (por usuário).
    hiddenPanelFields() {
      return (
        this.$store.getters.getUISettings?.contact_panel_hidden_fields || []
      );
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.contactIdentities = [];
        this.showOtherIdentities = false;
        this.$store.dispatch('contacts/fetchContactableInbox', id);
        this.fetchContactIdentities();
      },
      immediate: true,
    },
    hasContactIdentities() {
      this.fetchContactIdentities();
    },
  },
  methods: {
    identityIcon(identity) {
      return identity.identity_type === 'phone'
        ? 'i-ph-phone'
        : 'i-ph-envelope';
    },
    // O rect do gatilho vem da barra de ações: quem desenha o popover é o
    // painel dono, e sem a âncora ele voltaria a ser posicionado por número
    // mágico.
    onScheduleClick(triggerRect) {
      this.$emit('schedule', triggerRect || null);
    },
    identityHref(identity) {
      if (identity.identity_type === 'phone') return `tel:${identity.value}`;
      if (identity.identity_type === 'email') return `mailto:${identity.value}`;
      return '';
    },
    // Mesma regra da linha principal: telefone = bandeira + formato nacional.
    identityDisplayValue(identity) {
      return identity.identity_type === 'phone'
        ? formatPhoneDisplay(identity.value)
        : identity.value;
    },
    async copyIdentity(identity) {
      await copyTextToClipboard(identity.value);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    async fetchContactIdentities() {
      const contactId = this.contact?.id;
      this.identityRequestToken += 1;
      const requestToken = this.identityRequestToken;

      if (!this.hasContactIdentities || !contactId) {
        this.contactIdentities = [];
        this.isLoadingIdentities = false;
        return;
      }
      this.isLoadingIdentities = true;
      try {
        const { data } = await ContactAPI.getIdentities(contactId);
        if (
          requestToken !== this.identityRequestToken ||
          String(this.contact?.id) !== String(contactId)
        ) {
          return;
        }
        this.contactIdentities = Array.isArray(data?.payload)
          ? data.payload
          : [];
      } catch (error) {
        if (requestToken === this.identityRequestToken) {
          this.contactIdentities = [];
        }
      } finally {
        if (requestToken === this.identityRequestToken) {
          this.isLoadingIdentities = false;
        }
      }
    },
    async makeIdentityPrimary(identity) {
      const contactId = this.contact.id;
      try {
        await ContactAPI.makeIdentityPrimary(contactId, identity.id);
        if (String(this.contact?.id) !== String(contactId)) return;

        useAlert(this.$t('CONTACT_PANEL.IDENTITIES.PRIMARY_UPDATED'));
        await this.$store.dispatch('contacts/show', { id: contactId });
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
        if (String(this.contact?.id) !== String(contactId)) return;
        await this.fetchContactIdentities();
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            this.$t('CONTACT_PANEL.IDENTITIES.PRIMARY_FAILED')
        );
      }
    },
    async onCallClick() {
      try {
        // StackZap outbound: backend resolves device/target from conversation.
        await startOutboundStackZap({
          conversationId: this.currentChat?.id,
          store: this.$store,
        });
      } catch (e) {
        // C25: never surface raw provider/network text (URL, secrets, e.message,
        // arbitrary response.data.error). Only a fixed allowlist of known API
        // error_code values may reach the agent UI; everything else is generic.
        const SAFE_CODES = {
          not_configured: true,
          call_failed: true,
          provider_error: true,
          timeout: true,
          invalid_id: true,
          invalid_base_url: true,
        };
        const code = e?.response?.data?.error_code;
        let safe = this.$t('CONTACT_PANEL.CALL_FAILED');
        if (code === 'target_unreachable') {
          safe = this.$t('CONTACT_PANEL.CALL_UNREACHABLE');
        } else if (typeof code === 'string' && SAFE_CODES[code]) {
          safe = code;
        }
        useAlert(safe);
      }
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    // SiteUp (Phase C-3): visibilidade dos campos do painel, salva por usuário.
    isFieldVisible(key) {
      return !this.hiddenPanelFields.includes(key);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard_index') {
          // SiteUp: 'contacts_dashboard' não existe no router; o push lançava
          // dentro do try e o toast de erro aparecia depois do de sucesso.
          this.$router.push({
            name: 'contacts_dashboard_index',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    startEditingName() {
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    saveNameEdit() {
      if (!this.isEditingName) return;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <div
    class="contact--profile"
    :class="{ 'text-[15px]': isWorkspaceV2 }"
    data-testid="focus-spot-contact-card"
  >
    <!-- SITEUP V2 / G2: card --panel raio 16 (mockup 03 cprofile) -->
    <PanelCard :padding="isWorkspaceV2 ? 'p-4' : 'p-3.5'">
      <div class="flex w-full flex-col gap-1.5 text-left rtl:text-right">
        <!-- Mockup: avatar + nome Display 800; ações só lápis/lixeira -->
        <div class="flex w-full min-w-0 items-start gap-3">
          <Avatar
            v-if="showAvatar"
            :src="contact.thumbnail"
            :name="contact.name"
            :status="contact.availability_status"
            :size="64"
            class="shrink-0"
          />
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <InlineInput
              v-if="isEditingName"
              ref="nameInput"
              v-model="editName"
              :custom-input-class="
                isWorkspaceV2
                  ? '!text-xl !font-medium'
                  : '!text-[17px] !font-medium'
              "
              class="!w-fit min-w-0"
              @enter-press="saveNameEdit"
              @escape-press="cancelNameEdit"
              @blur="saveNameEdit"
            />
            <div v-else class="flex min-w-0 flex-wrap items-center gap-x-2">
              <h3
                class="my-0 min-w-0 cursor-pointer break-words text-[17px] font-medium capitalize leading-tight text-n-slate-12 hover:text-n-slate-12/80"
                :class="{ '!text-xl': isWorkspaceV2 }"
                :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
                @click="startEditingName"
              >
                {{ contact.name }}
              </h3>
              <!-- SiteUp V2 (2026-08-11): "Conta comercial" era uma linha solta
                   no meio de dez outras. É um atributo da IDENTIDADE — quem é
                   esta pessoa —, então vira selo colado ao nome. -->
              <span
                v-if="showBusinessBadge"
                v-tooltip.top="$t('CONTACT_PANEL.WHATSAPP_BUSINESS_ACCOUNT')"
                data-testid="contact-business-badge"
                class="inline-flex shrink-0 items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-bold text-n-slate-11"
              >
                <span class="i-ph-briefcase size-3 shrink-0" />
                {{ $t('CONTACT_PANEL.BUSINESS_BADGE') }}
              </span>
            </div>
            <!-- SiteUp F1.5b: de onde este contato veio, gravado uma vez na
                 primeira chegada (CtwaReferralProcessor). Subiu para junto do
                 nome: é a primeira pergunta de quem abre o painel, e no meio da
                 lista de dados ela se perdia. -->
            <component
              :is="additionalAttributes.origin_url ? 'a' : 'div'"
              v-if="contactOriginLabel && isFieldVisible('origin')"
              :href="additionalAttributes.origin_url || undefined"
              :target="additionalAttributes.origin_url ? '_blank' : undefined"
              :rel="
                additionalAttributes.origin_url
                  ? 'noopener noreferrer'
                  : undefined
              "
              data-testid="contact-origin-line"
              class="flex min-w-0 flex-wrap items-baseline gap-x-1 text-[12.5px] leading-snug"
              :class="{
                'hover:underline': additionalAttributes.origin_url,
                '!text-[13px]': isWorkspaceV2,
              }"
            >
              <span class="shrink-0 text-n-slate-11">
                {{ $t('CONTACT_PANEL.ARRIVED_FROM') }}
              </span>
              <!-- Quebra em vez de truncar: o nome da campanha é a informação,
                   e "Anúncio da Meta · …" não diz de qual campanha veio. O
                   ícone de link fica DENTRO do texto — solto ele caía sozinho
                   numa terceira linha. -->
              <span class="min-w-0 font-semibold text-n-slate-12">
                {{ contactOriginLabel }}
                <span
                  v-if="additionalAttributes.origin_url"
                  class="i-ph-arrow-square-out ml-0.5 inline-block size-3 shrink-0 align-[-1px] text-n-slate-11"
                />
              </span>
            </component>
            <!-- Esta linha ficou sendo só EDITAR o cadastro e EXCLUIR — duas
                 ações sobre o REGISTRO. Agendar subiu para a barra de ações
                 (V2, 2026-08-11), junto das outras coisas que se faz COM a
                 pessoa. Excluir fica por último e separado: ação destrutiva
                 não encosta em ação comum. -->
            <div class="flex items-center gap-1.5">
              <NextButton
                v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
                :aria-label="$t('EDIT_CONTACT.BUTTON_LABEL')"
                icon="i-ph-pencil-simple"
                slate
                faded
                sm
                @click="toggleEditModal"
              />
              <NextButton
                v-if="isAdmin"
                v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
                :aria-label="$t('DELETE_CONTACT.BUTTON_LABEL')"
                icon="i-ph-trash"
                slate
                faded
                sm
                ruby
                class="ml-1"
                :disabled="uiFlags.isDeleting"
                @click="toggleDeleteModal"
              />
            </div>
          </div>
        </div>

        <!-- SiteUp V2 (2026-08-11): as ações vêm ANTES dos dados. Quem abre o
             painel no meio de um atendimento quer agir; ler o cadastro é o
             caso menos frequente. Antes disto elas eram ícones de 16px
             escondidos na linha do telefone. -->
        <ContactActionBar
          class="mt-1"
          :contact="contact"
          :can-call="canCall"
          :can-schedule="canSchedule"
          :ad-attributes="adAttributes"
          @call="onCallClick"
          @schedule="onScheduleClick"
          @show-ad-info="showAdInfo = !showAdInfo"
        />
        <!-- De qual anúncio esta conversa veio. Fechado por padrão: é contexto
             de consulta, não algo que precise ocupar o painel o tempo todo. -->
        <div
          v-if="showAdInfo"
          data-testid="contact-ad-info"
          class="w-full rounded-xl bg-n-alpha-2 p-3"
        >
          <AdOriginDetails :attrs="adAttributes" show-headline />
          <div
            v-if="primaryKanbanDescription"
            data-testid="contact-ad-info-card-summary"
            class="mt-2.5 border-t border-n-weak pt-2.5"
          >
            <p
              class="m-0 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.ACTIONS.CARD_SUMMARY') }}
            </p>
            <p
              class="m-0 mt-1 whitespace-pre-line text-xs leading-snug text-n-slate-12"
            >
              {{ primaryKanbanDescription }}
            </p>
          </div>
        </div>

        <div class="flex w-full min-w-0 flex-col items-start gap-1">
          <p
            v-if="additionalAttributes.description"
            class="mb-0.5 break-words text-[13px] text-n-slate-11"
          >
            {{ additionalAttributes.description }}
          </p>
          <!-- SiteUp V2 (2026-08-11): um card só para os dados de contato. Antes
               eram linhas soltas separadas por um filete; o card as agrupa como
               "o cadastro" e separa do que é ação. -->
          <div
            data-testid="contact-data-card"
            class="mt-1 flex w-full flex-col items-stretch gap-0 rounded-2xl bg-n-alpha-1 p-3"
          >
            <ContactInfoRow
              v-if="contact.email && isFieldVisible('email')"
              :href="contact.email ? `mailto:${contact.email}` : ''"
              :value="contact.email"
              icon="mail"
              emoji="✉️"
              :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
              show-copy
              editable
              @update="value => onFieldUpdate('email', value)"
            />
            <!-- SiteUp V2: a bandeira é o rótulo — o texto "Número de
                 telefone" saía mais caro que o dado (truncava o número).
                 WhatsApp e Ligar saíram desta linha em 2026-08-11: viraram a
                 barra de ações no topo. Aqui é DADO, não ação. -->
            <ContactInfoRow
              v-if="contact.phone_number && isFieldVisible('phone')"
              :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
              :value="phoneDisplayValue"
              :copy-value="contact.phone_number"
              icon="call"
              emoji="📞"
              show-copy
            />
            <!-- SiteUp: endereço/e-mail que a própria conta comercial publica no
                 WhatsApp. Vem do Waha::ContactEnrichmentJob, não do agente —
                 por isso não são editáveis aqui: editar não propagaria. -->
            <ContactInfoRow
              v-if="
                additionalAttributes.waha_business_address &&
                isFieldVisible('whatsapp_business')
              "
              :value="additionalAttributes.waha_business_address"
              icon="map"
              emoji="🏪"
              :title="$t('CONTACT_PANEL.WHATSAPP_BUSINESS_ADDRESS')"
              show-copy
            />
            <ContactInfoRow
              v-if="
                additionalAttributes.waha_business_email &&
                isFieldVisible('whatsapp_business')
              "
              :href="`mailto:${additionalAttributes.waha_business_email}`"
              :value="additionalAttributes.waha_business_email"
              icon="mail"
              emoji="📧"
              :title="$t('CONTACT_PANEL.WHATSAPP_BUSINESS_EMAIL')"
              show-copy
            />
            <ContactInfoRow
              v-if="contact.identifier && isFieldVisible('identifier')"
              :value="contact.identifier"
              icon="contact-identify"
              emoji="🪪"
              :title="$t('CONTACT_PANEL.IDENTIFIER')"
            />
            <ContactInfoRow
              v-if="
                additionalAttributes.company_name && isFieldVisible('company')
              "
              :value="additionalAttributes.company_name"
              icon="building-bank"
              emoji="🏢"
              :title="$t('CONTACT_PANEL.COMPANY')"
              editable
              @update="
                value =>
                  updateContactField({
                    additional_attributes: {
                      ...additionalAttributes,
                      company_name: value,
                    },
                  })
              "
            />
            <ContactInfoRow
              v-if="
                (location || additionalAttributes.location) &&
                isFieldVisible('location')
              "
              :value="location || additionalAttributes.location"
              icon="map"
              emoji="🌍"
              :title="$t('CONTACT_PANEL.LOCATION')"
            />
            <!-- SiteUp (Phase C-2/3): endereço completo (do form de editar contato) -->
            <ContactInfoRow
              v-if="formattedAddress && isFieldVisible('address')"
              :value="formattedAddress"
              icon="map"
              emoji="📍"
              :title="$t('CONTACT_PANEL.ADDRESS')"
            />
            <SocialIcons
              v-if="isFieldVisible('social')"
              :social-profiles="socialProfiles"
            />
            <div
              v-if="hasContactIdentities && otherIdentities.length"
              class="w-full pt-1"
            >
              <button
                type="button"
                class="flex h-7 w-full items-center gap-2 rounded-md px-1 text-left text-sm text-n-slate-11 hover:bg-n-alpha-1"
                @click="showOtherIdentities = !showOtherIdentities"
              >
                <span class="i-ph-address-book size-3.5 shrink-0" />
                <span class="min-w-0 flex-1 truncate">
                  {{ identitySummaryLabel }}
                </span>
                <span
                  class="i-ph-caret-down size-3.5 shrink-0 transition-transform"
                  :class="{ 'rotate-180': showOtherIdentities }"
                />
              </button>
              <div v-if="showOtherIdentities" class="mt-1 flex flex-col gap-1">
                <div
                  v-for="identity in otherIdentities"
                  :key="identity.id"
                  class="group/identity flex min-h-7 w-full items-center gap-2 rounded-md px-1 text-sm text-n-slate-11 hover:bg-n-alpha-1"
                >
                  <span
                    class="size-3.5 shrink-0"
                    :class="[identityIcon(identity)]"
                  />
                  <a
                    :href="identityHref(identity)"
                    class="min-w-0 flex-1 truncate hover:underline"
                    :title="identity.value"
                  >
                    {{ identityDisplayValue(identity) }}
                  </a>
                  <span
                    v-if="identity.is_primary"
                    class="shrink-0 rounded bg-n-alpha-2 px-1.5 py-0.5 text-xxs text-n-slate-11"
                  >
                    {{ $t('CONTACT_PANEL.IDENTITIES.PRIMARY') }}
                  </span>
                  <NextButton
                    v-tooltip.top="$t('CONTACT_PANEL.IDENTITIES.COPY')"
                    ghost
                    xs
                    slate
                    icon="i-ph-clipboard"
                    @click.prevent="copyIdentity(identity)"
                  />
                  <NextButton
                    v-if="!identity.is_primary"
                    v-tooltip.top="$t('CONTACT_PANEL.IDENTITIES.MAKE_PRIMARY')"
                    ghost
                    xs
                    slate
                    icon="i-ph-star"
                    @click.prevent="makeIdentityPrimary(identity)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- SiteUp: três caixas (Participantes / Time / Etiquetas) sem shell
             "Ações da conversa" — sem accordion/toggle/chevron. -->
        <div
          v-if="currentChat?.id"
          class="conversation--actions mt-2 w-full border-t border-n-weak pt-2"
        >
          <ConversationAction :conversation-id="currentChat.id" />
        </div>
        <EditContact
          v-if="showEditModal"
          :show="showEditModal"
          :contact="contact"
          @cancel="toggleEditModal"
        />
      </div>
    </PanelCard>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>
